import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CheckCircle, XCircle, Users, Target, Trophy,
  BookOpen, Brain, Sparkles, FolderOpen, RefreshCw, Award,
  ChevronDown, Search
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const MILESTONES = [
  { key: 'profile', label: 'Profile', icon: Users, short: 'Profile' },
  { key: 'interests', label: 'Interests', icon: Sparkles, short: 'Interests' },
  { key: 'goals_set_3', label: '3+ Goals', icon: Target, short: 'Goals' },
  { key: 'goals_completed_2', label: '2+ Done', icon: CheckCircle, short: 'Done' },
  { key: 'course_completed', label: 'Course', icon: BookOpen, short: 'Course' },
  { key: 'win_posted', label: 'Win Posted', icon: Trophy, short: 'Win' },
  { key: 'project_started', label: 'Project', icon: FolderOpen, short: 'Project' },
  { key: 'checkin_done', label: 'Check-in', icon: CheckCircle, short: 'Check-in' },
  { key: 'assessment_done', label: 'Assessment', icon: Brain, short: 'SJT' },
];

const CompletionTracker = ({ user }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, complete, incomplete, not_logged_in

  useEffect(() => {
    fetchTracker();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTracker = async () => {
    try {
      const res = await fetch(`${API}/api/platform/completion-tracker`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  if (loading) return <div className="text-center py-16"><RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-300" /></div>;
  if (!data) return <div className="text-center py-16 text-gray-400">No data available</div>;

  const s = data.summary;
  let filtered = data.youth || [];
  
  if (search) {
    filtered = filtered.filter(y => y.name.toLowerCase().includes(search.toLowerCase()) || y.email.toLowerCase().includes(search.toLowerCase()));
  }
  if (filter === 'complete') filtered = filtered.filter(y => y.completion_pct === 100);
  if (filter === 'incomplete') filtered = filtered.filter(y => y.completion_pct > 0 && y.completion_pct < 100);
  if (filter === 'not_logged_in') filtered = filtered.filter(y => !y.logged_in);
  if (filter === 'not_started') filtered = filtered.filter(y => y.completion_pct === 0);

  return (
    <div className="min-h-screen pb-8" style={{ backgroundColor: '#F5F3EF' }}>
      <div style={{ background: 'linear-gradient(135deg, #1B2D4F 0%, #1E6B7B 100%)' }} className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-white/20 hover:bg-white/30"><ArrowLeft className="w-5 h-5 text-white" /></button>
            <div>
              <h1 className="text-2xl font-bold" style={{ color: 'white' }}>Completion Tracker</h1>
              <p style={{ color: 'rgba(255,255,255,0.7)' }} className="text-sm">Track which youth have completed key platform milestones</p>
            </div>
          </div>
          
          {/* Summary KPIs */}
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            {[
              { l: 'Total', v: s.total_youth, c: 'white' },
              { l: 'Logged In', v: s.logged_in, c: '#10B981' },
              { l: 'Avg Complete', v: `${s.avg_completion}%`, c: '#E86C3D' },
              { l: '100% Done', v: s.fully_complete, c: '#8B5CF6' },
              { l: 'Not Started', v: s.total_youth - s.logged_in, c: '#EF4444' },
            ].map(k => (
              <div key={k.l} className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
                <p className="text-xl font-bold" style={{ color: k.c }}>{k.v}</p>
                <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.7)' }}>{k.l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Milestone Summary Bar */}
        <Card className="p-4 rounded-2xl bg-white mb-5">
          <h3 className="font-bold text-xs text-gray-500 uppercase mb-3">Milestone Completion Rates</h3>
          <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
            {MILESTONES.map(m => {
              const count = s[m.key] || 0;
              const pct = s.total_youth > 0 ? Math.round(count / s.total_youth * 100) : 0;
              return (
                <div key={m.key} className="text-center p-2 rounded-xl bg-gray-50">
                  <m.icon className={`w-4 h-4 mx-auto mb-1 ${pct > 50 ? 'text-green-500' : pct > 0 ? 'text-amber-500' : 'text-gray-300'}`} />
                  <p className="text-lg font-bold text-gray-900">{pct}%</p>
                  <p className="text-[9px] text-gray-500">{m.short}</p>
                  <p className="text-[9px] text-gray-400">{count}/{s.total_youth}</p>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Search & Filter */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search youth..." className="pl-9 rounded-xl bg-white" />
          </div>
          {[
            { v: 'all', l: 'All' },
            { v: 'not_logged_in', l: 'Not Logged In' },
            { v: 'not_started', l: 'Not Started' },
            { v: 'incomplete', l: 'In Progress' },
            { v: 'complete', l: '100% Complete' },
          ].map(f => (
            <Button key={f.v} onClick={() => setFilter(f.v)} variant={filter === f.v ? 'default' : 'outline'} size="sm"
              className={`rounded-xl text-xs ${filter === f.v ? 'bg-[#1B2D4F] text-white' : ''}`}>
              {f.l}
            </Button>
          ))}
        </div>

        {/* Youth Table */}
        <Card className="rounded-2xl bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-600">
                  <th className="p-3 text-left sticky left-0 bg-gray-50">Youth</th>
                  <th className="p-3 text-center">Status</th>
                  <th className="p-3 text-center">Progress</th>
                  {MILESTONES.map(m => (
                    <th key={m.key} className="p-2 text-center" title={m.label}>
                      <m.icon className="w-3.5 h-3.5 mx-auto text-gray-400" />
                      <span className="text-[8px]">{m.short}</span>
                    </th>
                  ))}
                  <th className="p-3 text-center">Pts</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((y, i) => (
                  <tr key={y.id} className="border-t border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="p-3 sticky left-0 bg-white">
                      <p className="font-semibold text-gray-900">{y.name}</p>
                      <p className="text-[10px] text-gray-400">{y.work_site || y.email}</p>
                    </td>
                    <td className="p-3 text-center">
                      {y.logged_in ? (
                        <Badge className="bg-green-100 text-green-700 text-[10px]">Active</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700 text-[10px]">Not Logged In</Badge>
                      )}
                    </td>
                    <td className="p-3 text-center">
                      <div className="flex items-center gap-2 min-w-[80px]">
                        <Progress value={y.completion_pct} className="h-2 flex-1" />
                        <span className={`text-xs font-bold ${y.completion_pct === 100 ? 'text-green-600' : y.completion_pct > 50 ? 'text-amber-600' : 'text-gray-400'}`}>
                          {y.completion_pct}%
                        </span>
                      </div>
                    </td>
                    {MILESTONES.map(m => (
                      <td key={m.key} className="p-2 text-center">
                        {y.completion[m.key] ? (
                          <CheckCircle className="w-4 h-4 mx-auto text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 mx-auto text-gray-200" />
                        )}
                      </td>
                    ))}
                    <td className="p-3 text-center font-bold text-orange-600">{y.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-8 text-gray-400">No youth match your filter</div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default CompletionTracker;
